import React, { useState } from 'react';

import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import produce from 'immer';
import _ from 'lodash';

import { ReactComponent as AddIcon } from '../../../common/assets/icons/add_new_icon.svg';
import { ReactComponent as CancelIcon } from '../../../common/assets/icons/cancel_icon_gray.svg';
import { ReactComponent as ChevronDownIcon } from '../../../common/assets/icons/chevron_down_icon_blue.svg';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../common/hooks/reduxHooks';
import {
  setFilterExclude,
  setFilterInclude,
} from '../../../common/stores/modelSlice';

type Props = {
  type: 'include' | 'exclude';
};

const FiltersContainerCell: React.FC<Props> = ({ type }) => {
  const [filter, setFilter] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [items, setItems] = useState<string[]>([]);

  const dispatch = useAppDispatch();

  const handleRotate = () => setIsOpen(!isOpen);

  const rotate = isOpen ? 'rotate(180deg)' : 'rotate(0)';

  const { filterInclude, filterExclude } = useAppSelector(
    (state) => state.model
  );

  const filtersLength =
    type === 'include' ? filterInclude.length : filterExclude.length;

  React.useEffect(() => {
    const filters = type === 'exclude' ? filterExclude : filterInclude;
    setItems(filters);
  }, [filterExclude, filterInclude, type]);

  const addItem = () => {
    const newItem = filter.trim();
    setFilter('');
    if (items.includes(newItem) || _.isEmpty(newItem)) return;
    setItems([...items, newItem]);
    applyFilter([...items, newItem]);
  };

  const applyFilter = (filters: string[]) => {
    if (type === 'exclude') {
      dispatch(setFilterExclude(filters));
    } else {
      dispatch(setFilterInclude(filters));
    }
  };

  const removeItem = (item: string) => {
    const deletedItemsArray = produce(items, (draft) => {
      const index = draft.findIndex((str) => str === item);
      if (index !== -1) draft.splice(index, 1);
    });
    setItems(deletedItemsArray);
    applyFilter(deletedItemsArray);
  };

  const onKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.code === 'Enter') {
      addItem();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flex: 1,
          justifyContent: 'space-between',
          marginTop: '10px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
          }}
        >
          {filtersLength !== 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'paleOrange.main',
                width: '30px',
                height: '30px',
                borderRadius: '15px',
                marginRight: '10px',
              }}
            >
              <Typography color='appOrange.main'>{filtersLength}</Typography>
            </Box>
          )}
          <Typography variant='h6' color='primary'>
            Results should {type}
          </Typography>
        </Box>
        <IconButton
          sx={{
            transform: rotate,
            transition: 'all 0.25s ease-in-out',
            alignSelf: 'baseline',
          }}
          onClick={() => handleRotate()}
          className={`${isOpen ? 'icon-selected' : 'icon'}`}
        >
          <ChevronDownIcon />
        </IconButton>
      </Box>
      <Typography
        sx={{ alignSelf: 'flex-start' }}
        color='textFlorenceSecondary.main'
      >
        You can write the word or phrase you want to {type} in search then click
        plus icon to add
      </Typography>
      {isOpen && (
        <Box sx={{ marginTop: '25px' }}>
          <TextField
            placeholder='Description'
            variant='standard'
            fullWidth
            type='text'
            value={filter}
            onChange={(e: any) => setFilter(e.target.value)}
            onKeyUp={onKeyUp}
            InputProps={{
              endAdornment: (
                <InputAdornment position='start'>
                  <IconButton
                    onClick={() => {
                      addItem();
                    }}
                    sx={{ marginBottom: '10px' }}
                  >
                    <AddIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
            }}
          >
            {items?.map((f, index) => {
              return (
                <Box
                  sx={{
                    backgroundColor: '#F0EDE6',
                    borderRadius: '2vh',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    pl: '10px',
                    pr: '15px',
                    mt: '15px',
                    mr: '15px',
                  }}
                  key={index}
                >
                  <Typography
                    sx={{ wordBreak: 'break-all' }}
                    color='text.primary'
                  >
                    {f}
                  </Typography>
                  <IconButton onClick={() => removeItem(f)}>
                    <CancelIcon />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FiltersContainerCell;
